import data from '@emoji-mart/data'

// Map :shortcode: → native emoji, built once from the emoji-mart dataset that the
// picker already ships. Includes the canonical id (e.g. "smile") and any aliases
// (e.g. "satisfied" → "laughing").
interface EmojiMartData {
  emojis: Record<string, { skins?: { native?: string }[] }>
  aliases?: Record<string, string>
}

const shortcodes: Record<string, string> = (() => {
  const d = data as unknown as EmojiMartData
  const map: Record<string, string> = {}
  for (const [id, emoji] of Object.entries(d.emojis ?? {})) {
    const native = emoji.skins?.[0]?.native
    if (native) map[id] = native
  }
  for (const [alias, id] of Object.entries(d.aliases ?? {})) {
    const native = map[id]
    if (native) map[alias] = native
  }
  return map
})()

export function emojiFromShortcode(code: string): string | null {
  return shortcodes[code.toLowerCase()] ?? null
}

// Shortcodes use letters, digits, +, - and _ between two colons.
const SHORTCODE_RE = /:([a-z0-9_+-]+):/gi

/** Replace every known :shortcode: in a string (used as a send-time safety net). */
export function replaceShortcodes(text: string): string {
  return text.replace(SHORTCODE_RE, (whole, code) => emojiFromShortcode(code) ?? whole)
}

/**
 * If the text immediately before the caret just completed a known :shortcode:,
 * return the text with it replaced by the emoji and the new caret position.
 * Requires a boundary before the opening colon so we don't fire inside URLs etc.
 */
export interface ShortcodeMatch { code: string; native: string }

/**
 * If the caret sits inside a `:partial` token (no closing colon yet), return the
 * token so an autocomplete can be shown. The colon must start the line or follow
 * whitespace (so "12:30" / "http://" don't trigger), and at least 2 chars typed.
 */
export function getShortcodeQuery(text: string, caret: number): { start: number; query: string } | null {
  const upto = text.slice(0, caret)
  const colon = upto.lastIndexOf(':')
  if (colon === -1) return null
  if (colon > 0 && !/\s/.test(text[colon - 1]!)) return null
  const query = upto.slice(colon + 1)
  if (query.length < 2 || query.length > 40) return null
  if (!/^[a-z0-9_+-]+$/i.test(query)) return null
  return { start: colon, query }
}

/** Rank matches: exact code, then prefix, then substring; deduped by emoji. */
export function searchShortcodes(query: string, limit = 8): ShortcodeMatch[] {
  const q = query.toLowerCase()
  if (!q) return []
  const exact: ShortcodeMatch[] = []
  const prefix: ShortcodeMatch[] = []
  const contains: ShortcodeMatch[] = []
  for (const [code, native] of Object.entries(shortcodes)) {
    if (code === q) exact.push({ code, native })
    else if (code.startsWith(q)) prefix.push({ code, native })
    else if (code.includes(q)) contains.push({ code, native })
  }
  const seen = new Set<string>()
  const out: ShortcodeMatch[] = []
  for (const bucket of [exact, prefix, contains]) {
    bucket.sort((a, b) => a.code.length - b.code.length)
    for (const match of bucket) {
      if (seen.has(match.native)) continue
      seen.add(match.native)
      out.push(match)
      if (out.length >= limit) return out
    }
  }
  return out
}

export function shortcodeAtCaret(value: string, caret: number): { text: string; caret: number } | null {
  const upto = value.slice(0, caret)
  const match = /(^|[^a-z0-9])(:([a-z0-9_+-]+):)$/i.exec(upto)
  if (!match) return null
  const native = emojiFromShortcode(match[3]!)
  if (!native) return null
  const token = match[2]! // the :code: part (without the leading boundary char)
  const start = caret - token.length
  return { text: value.slice(0, start) + native + value.slice(caret), caret: start + native.length }
}
